// Share utilities for social media and other sharing options

/**
 * Get the current page URL
 */
export const getCurrentUrl = () => {
  return window.location.href;
};

/**
 * Get the current page title
 */
export const getCurrentTitle = () => {
  return document.title;
};

/**
 * Share to Facebook
 */
export const shareToFacebook = (url, title) => {
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`;
  window.open(facebookUrl, '_blank', 'width=600,height=400');
};

/**
 * Share to Twitter
 */
export const shareToTwitter = (url, title) => {
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
  window.open(twitterUrl, '_blank', 'width=600,height=400');
};

/**
 * Share to WhatsApp
 */
export const shareToWhatsApp = (url, title) => {
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${title} - ${url}`)}`;
  window.open(whatsappUrl, '_blank');
};

/**
 * Share arbitrary text to WhatsApp
 */
export const shareTextToWhatsApp = (text) => {
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(whatsappUrl, '_blank');
};

/**
 * Share to Instagram (opens Instagram in new tab - user can then share manually)
 */
export const shareToInstagram = (url, title) => {
  // Instagram doesn't have direct sharing API, so we open Instagram and show instructions
  const instagramUrl = 'https://www.instagram.com/';
  window.open(instagramUrl, '_blank');
  
  // Show a message to the user
  return {
    message: 'Instagram opened! You can now create a story or post and share your recipe manually.',
    url: url,
    title: title
  };
};

/**
 * Share to Pinterest
 */
export const shareToPinterest = (url, title, imageUrl) => {
  const pinterestUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(title)}${imageUrl ? `&media=${encodeURIComponent(imageUrl)}` : ''}`;
  window.open(pinterestUrl, '_blank', 'width=600,height=400');
};

/**
 * Share to LinkedIn
 */
export const shareToLinkedIn = (url, title) => {
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  window.open(linkedinUrl, '_blank', 'width=600,height=400');
};

/**
 * Copy link to clipboard
 */
export const copyToClipboard = async (url, title) => {
  try {
    const textToCopy = `${title} - ${url}`;
    await navigator.clipboard.writeText(textToCopy);
    return { success: true, message: 'Link copied to clipboard!' };
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = `${title} - ${url}`;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return { success: true, message: 'Link copied to clipboard!' };
    } catch (fallbackError) {
      document.body.removeChild(textArea);
      return { success: false, message: 'Failed to copy link. Please copy manually.' };
    }
  }
};

/**
 * Share via email
 */
export const shareViaEmail = (url, title) => {
  const subject = `Check out this recipe: ${title}`;
  const body = `I found this amazing recipe and thought you might like it:\n\n${title}\n${url}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = emailUrl;
};

/**
 * Share via SMS (mobile)
 */
export const shareViaSMS = (url, title) => {
  const text = `Check out this recipe: ${title} - ${url}`;
  const smsUrl = `sms:?body=${encodeURIComponent(text)}`;
  window.location.href = smsUrl;
};

/**
 * Native Web Share API (if supported)
 */
export const shareNative = async (url, title, text) => {
  if (navigator.share) {
    try {
      await navigator.share({
        title: title,
        text: text || title,
        url: url
      });
      return { success: true, message: 'Shared successfully!' };
    } catch (error) {
      if (error.name !== 'AbortError') {
        return { success: false, message: 'Failed to share. Please try another method.' };
      }
      return { success: true, message: 'Share cancelled.' };
    }
  }
  return { success: false, message: 'Native sharing not supported on this device.' };
};

/**
 * Native share for text (no URL)
 */
export const shareNativeText = async (title, text) => {
  if (navigator.share) {
    try {
      await navigator.share({ title, text: text || title });
      return { success: true, message: 'Shared successfully!' };
    } catch (error) {
      if (error.name !== 'AbortError') {
        return { success: false, message: 'Failed to share. Please try another method.' };
      }
      return { success: true, message: 'Share cancelled.' };
    }
  }
  return { success: false, message: 'Native sharing not supported on this device.' };
};

/**
 * Open a print-friendly window and print provided HTML
 */
export const printHtml = (html, title = 'Print') => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif; padding: 24px; color: #222; }
      h1 { margin: 0 0 12px; font-size: 22px; }
      .meta { color: #555; margin-bottom: 16px; font-size: 12px; }
      ul { padding-left: 20px; }
      li { margin: 6px 0; }
      .done { text-decoration: line-through; color: #777; }
      @media print { button { display: none; } }
    </style>
  </head><body>
    ${html}
  </body></html>`);
  printWindow.document.close();
  printWindow.focus();
  // Small timeout ensures styles apply before print dialog
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 100);
};

const shareUtils = {
  getCurrentUrl,
  getCurrentTitle,
  shareToFacebook,
  shareToTwitter,
  shareToWhatsApp,
  shareToInstagram,
  shareToPinterest,
  shareToLinkedIn,
  copyToClipboard,
  shareViaEmail,
  shareViaSMS,
  shareNative,
  shareNativeText,
  shareTextToWhatsApp,
  printHtml
};

export default shareUtils;
